import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Buildings, UserFocus, MapTrifold, Star, ShieldCheck, ArrowRight, Lightning, BellRinging, CheckCircle, ArrowsClockwise } from "@phosphor-icons/react";

const HERO = "https://images.unsplash.com/photo-1566245024852-04fbf7842ce9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxzZWN1cml0eSUyMGd1YXJkJTIwcHJvZmVzc2lvbmFsJTIwZXZlbnR8ZW58MHx8fHwxNzgzMDg3Mjc3fDA&ixlib=rb-4.1.0&q=85";
const SEC = "https://images.unsplash.com/photo-1652739758426-56a564265f9e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxzZWN1cml0eSUyMGd1YXJkJTIwcHJvZmVzc2lvbmFsJTIwZXZlbnR8ZW58MHx8fHwxNzgzMDg3Mjc3fDA&ixlib=rb-4.1.0&q=85";
const COMP = "https://images.unsplash.com/photo-1766650552308-2c9ed710a798?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGNyb3dkJTIwZXZlbnQlMjBuaWdodHxlbnwwfHx8fDE3ODMwODcyNzd8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ jobs: 0, guards: 0, companies: 0 });

  useEffect(() => { api.get("/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-[88vh] flex items-center">
        <div className="absolute inset-0">
          <img src={HERO} alt="security" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0A0A0A]/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 w-full py-24">
          <div className="max-w-2xl animate-fade-up">
            <div className="inline-flex items-center gap-2 bg-[#E11D2A] text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-[0.15em] mb-6">
              <ShieldCheck weight="fill" size={16} /> NL · DE · EN
            </div>
            <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none text-white">{t("hero_title")}</h1>
            <p className="mt-6 text-lg text-zinc-200 max-w-xl">{t("hero_sub")}</p>
            <div className="mt-9 flex flex-wrap gap-4">
              {!user && (
                <>
                  <button data-testid="hero-find-jobs" onClick={() => navigate("/jobs")} className="bg-[#E11D2A] text-white hover:bg-[#B0121F] font-bold rounded-md px-6 py-3 transition-colors flex items-center gap-2">
                    {t("hero_cta_guard")} <ArrowRight weight="bold" size={18} />
                  </button>
                  <button data-testid="hero-post-job" onClick={() => navigate("/register")} className="bg-white/10 text-white border border-white/30 backdrop-blur hover:bg-white/20 font-semibold rounded-md px-6 py-3 transition-colors">
                    {t("hero_cta_company")}
                  </button>
                </>
              )}
              {user?.role === "guard" && (
                <button data-testid="hero-find-jobs" onClick={() => navigate("/jobs")} className="bg-[#E11D2A] text-white hover:bg-[#B0121F] font-bold rounded-md px-6 py-3 transition-colors flex items-center gap-2">
                  {t("hero_cta_guard")} <ArrowRight weight="bold" size={18} />
                </button>
              )}
              {user?.role === "company" && (
                <button data-testid="hero-post-job" onClick={() => navigate("/dashboard")} className="bg-[#E11D2A] text-white hover:bg-[#B0121F] font-bold rounded-md px-6 py-3 transition-colors flex items-center gap-2">
                  {t("hero_cta_company")} <ArrowRight weight="bold" size={18} />
                </button>
              )}
            </div>
            <div className="mt-12 flex gap-10">
              {[["stat_jobs", stats.jobs], ["stat_guards", stats.guards], ["stat_companies", stats.companies]].map(([k, v]) => (
                <div key={k}>
                  <div className="font-head font-black text-3xl text-white tabular-nums">{v}+</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-zinc-300 mt-1">{t(k)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Two audiences */}
      <section className="max-w-7xl mx-auto px-5 py-24">
        <div className={`grid gap-6 ${user ? "max-w-2xl mx-auto" : "md:grid-cols-2"}`}>
          {(!user || user.role === "company") && (
          <div className="relative overflow-hidden rounded-md border border-zinc-200 group">
            <img src={COMP} alt="companies" className="h-56 w-full object-cover" />
            <div className="p-8">
              <Buildings size={32} weight="duotone" className="text-[#E11D2A]" />
              <h2 className="font-head text-2xl font-bold tracking-tight mt-4">{t("for_companies")}</h2>
              <div className="mt-6 space-y-5">
                <Feature title={t("feat_c1")} desc={t("feat_c1d")} />
                <Feature title={t("feat_c2")} desc={t("feat_c2d")} />
              </div>
              {user?.role === "company" && (
                <button data-testid="home-post-job" onClick={() => navigate("/dashboard")} className="mt-7 bg-[#E11D2A] text-white hover:bg-[#B0121F] font-bold rounded-md px-6 py-2.5 transition-colors inline-flex items-center gap-2">{t("hero_cta_company")} <ArrowRight weight="bold" size={16} /></button>
              )}
            </div>
          </div>
          )}
          {(!user || user.role === "guard") && (
          <div className="relative overflow-hidden rounded-md border border-zinc-200">
            <img src={SEC} alt="guards" className="h-56 w-full object-cover" />
            <div className="p-8">
              <UserFocus size={32} weight="duotone" className="text-[#E11D2A]" />
              <h2 className="font-head text-2xl font-bold tracking-tight mt-4">{t("for_guards")}</h2>
              <div className="mt-6 space-y-5">
                <Feature title={t("feat_g1")} desc={t("feat_g1d")} icon={<MapTrifold />} />
                <Feature title={t("feat_g2")} desc={t("feat_g2d")} icon={<Star />} />
              </div>
              {user?.role === "guard" && (
                <button data-testid="home-find-jobs" onClick={() => navigate("/jobs")} className="mt-7 bg-[#E11D2A] text-white hover:bg-[#B0121F] font-bold rounded-md px-6 py-2.5 transition-colors inline-flex items-center gap-2">{t("hero_cta_guard")} <ArrowRight weight="bold" size={16} /></button>
              )}
            </div>
          </div>
          )}
        </div>
      </section>

      {/* Spoedklus highlight */}
      <section data-testid="spoed-highlight" className="bg-[#E11D2A] text-white py-20 relative overflow-hidden">
        <Lightning weight="fill" size={360} className="absolute -right-16 -top-10 text-white/10 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 relative">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-3 py-1 rounded-full text-xs font-bold uppercase tracking-[0.2em] mb-6">
            <Lightning weight="fill" size={15} /> {t("spoed_badge")}
          </div>
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-head text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-tight">{t("spoed_hl_title")}</h2>
              <p className="mt-5 text-lg text-white/90 max-w-xl">{t("spoed_hl_sub")}</p>
              <button data-testid="spoed-hl-cta" onClick={() => navigate("/register")} className="mt-8 bg-white text-[#E11D2A] hover:bg-zinc-100 font-bold rounded-md px-6 py-3 transition-colors inline-flex items-center gap-2">
                {t("spoed_hl_cta")} <ArrowRight weight="bold" size={18} />
              </button>
            </div>
            <div className="space-y-4">
              {[[BellRinging, t("spoed_hl_1")], [CheckCircle, t("spoed_hl_2")], [ArrowsClockwise, t("spoed_hl_3")]].map(([Icon, txt], i) => (
                <div key={i} className="flex items-center gap-4 bg-white/10 backdrop-blur border border-white/15 rounded-md px-5 py-4">
                  <Icon weight="fill" size={26} className="shrink-0" />
                  <span className="font-semibold">{txt}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#0A0A0A] text-white py-24">
        <div className="max-w-4xl mx-auto px-5">
          <div className="inline-flex items-center gap-2 text-[#E11D2A] text-xs font-bold uppercase tracking-[0.2em] mb-5">
            <ShieldCheck weight="fill" size={16} /> GuardLink
          </div>
          <h2 className="font-head text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-tight">
            De toekomst van flexibel samenwerken in de beveiligingsbranche.
          </h2>
          <div className="mt-8 space-y-5 text-zinc-300 text-lg leading-relaxed">
            <p>De beveiligingswereld vraagt om snelheid, betrouwbaarheid en continuïteit. Toch kosten het vinden van de juiste professionals en het invullen van openstaande diensten nog altijd onnodig veel tijd. GuardLink brengt daar verandering in.</p>
            <p>GuardLink is hét digitale platform waar beveiligingsbedrijven en zelfstandige beveiligingsprofessionals elkaar direct vinden. Bedrijven plaatsen eenvoudig beschikbare diensten, terwijl gekwalificeerde zzp'ers direct toegang krijgen tot nieuwe opdrachten en deze met één druk op de knop kunnen aannemen.</p>
            <p>Wij geloven dat de toekomst van de beveiligingsbranche draait om directe verbindingen, transparante samenwerking en maximale flexibiliteit. Geen eindeloze telefoontjes, losse WhatsApp-groepen of tijdrovende bemiddeling, maar één professioneel platform waarop vraag en aanbod in real-time samenkomen.</p>
            <p>Voor beveiligingsbedrijven betekent GuardLink sneller beschikken over betrouwbare professionals wanneer capaciteit nodig is. Voor zelfstandige beveiligers betekent het volledige vrijheid om opdrachten te kiezen die passen bij hun planning, ervaring en ambities.</p>
            <p>GuardLink is meer dan een platform. Het is een netwerk dat de beveiligingsbranche efficiënter, sterker en toekomstbestendig maakt. Door bedrijven en professionals rechtstreeks met elkaar te verbinden, creëren wij nieuwe kansen voor iedereen die actief is binnen de particuliere beveiliging.</p>
          </div>
          <p className="mt-10 font-head text-xl sm:text-2xl font-bold text-white border-l-4 border-[#E11D2A] pl-5">
            GuardLink. Waar beveiligingsbedrijven en beveiligingsprofessionals elkaar direct vinden. Zonder omwegen. Met vertrouwen.
          </p>
          <div className="mt-10">
            <button data-testid="vision-cta" onClick={() => navigate("/register")} className="bg-[#E11D2A] text-white hover:bg-[#B0121F] font-bold rounded-md px-6 py-3 transition-colors inline-flex items-center gap-2">
              {t("hero_cta_company")} <ArrowRight weight="bold" size={18} />
            </button>
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-200 py-10 text-center text-sm text-zinc-500">
        <div className="flex items-center justify-center gap-5 mb-3">
          <Link data-testid="footer-privacy" to="/privacy" className="hover:text-[#0A0A0A] transition-colors">Privacybeleid</Link>
          <span className="text-zinc-300">·</span>
          <Link data-testid="footer-terms" to="/terms" className="hover:text-[#0A0A0A] transition-colors">Algemene voorwaarden</Link>
        </div>
        © 2026 GuardLink · Beveiligingsmarktplaats
      </footer>
    </div>
  );
}

const Feature = ({ title, desc }) => (
  <div className="flex gap-3">
    <div className="w-1.5 h-1.5 rounded-full bg-[#E11D2A] mt-2 shrink-0" />
    <div>
      <div className="font-semibold">{title}</div>
      <div className="text-sm text-zinc-600 mt-1">{desc}</div>
    </div>
  </div>
);
